import https from '@sg/utils/httpC';

// Type definitions
interface StudioOverviewPayload {
  datatime?: string; // Optional: Format "YYYY-MM" e.g., "2025-10"
  type?: 'revenue' | 'profit'; // Required for trend API
}

interface StudioBreakdownPayload {
  type: string; // Required: 'revenue' or 'profit'
  datatime?: string; // Optional: Format "YYYY-MM" e.g., "2025-10"
}

interface UpdateStudioBreakdownPayload {
  type: string; // Required: 'revenue' or 'profit'
  datatime?: string; // Optional: Format "YYYY-MM" e.g., "2025-10"
  categories_to_create?: any[];
  categories_to_update?: any[];
  category_orders?: any[];
  studios_to_move?: any[];
  studio_orders?: any[];
  studio_notes?: any[];
  category_notes?: any[];
  categories_to_delete?: any[];
}

// Portfolio Overview
export const getStudioOverview = (payload: StudioOverviewPayload = {}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/overview',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getStudioOverviewTrend = (payload: StudioOverviewPayload = {}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/overview/trend',
  method: 'post',
  data: {
    ...payload,
    type: payload?.type || 'revenue',
  },
  rawData: true,
});

export const getStudioBreakdown = (payload: StudioBreakdownPayload): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/breakdown',
  method: 'post',
  data: payload,
  rawData: true,
});

export const updateStudioBreakdown = (payload: UpdateStudioBreakdownPayload): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/batch-update',
  method: 'post',
  data: payload,
  rawData: true,
})

// Board Governance Overview
export const getBoardGovernanceOverview = (payload: {} = {}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/calendar-meeting/overview',
  method: 'post',
  data: payload,
  rawData: true,
});

// export const getBoardGovernanceMonthly = (payload: any): Promise<any> => https({
//   url: '/api/v1/globalpedia_pc/mgmt/board_governance_monthly',
//   method: 'post',
//   data: payload,
//   rawData: true,
// });

export const getStudiosBoardGovernance = (payload: { studio_id: string }): Promise<any> => https({
  url: '/api/v1/globalpedia_pc/mgmt/board_governance',
  method: 'get',
  params: payload,
  rawData: true,
});

// Board Governance Get
// export const getBoardGovernance = (payload: {
//   meeting_id: string;
//   source: string;
// }): Promise<any> => https({
//   url: '/api/v1/mgmt_pc/studio/calendar-meeting/get',
//   method: 'post',
//   data: payload,
//   rawData: true,
// });

// Board Governance Save/Create
export const saveBoardGovernance = (payload: {
  meeting_id: string; // "0" for custom meetings, or actual meeting ID
  source: string; // bids/gpop/custom
  title?: string;
  description?: string;
  quarter?: string; // Required for Studios (format: 2025Q1, 2025Q2, etc.)
  month?: string; // Required for PortfolioOverview when meeting_id is "0" (format: 2025-01, 2025-02, etc.)
  studio_id?: string; // Required when meeting_id is "0"
  attach?: Array<{
    url: string;
    file_name: string;
  }>;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/calendar-meeting/save',
  method: 'post',
  data: payload,
  rawData: true,
});

// Board Governance Edit
export const editBoardGovernance = (payload: {
  meeting_id: string;
  date?: string; // format: 'YYYY-MM'
  summary_title?: string;
  description?: string;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/calendar-meeting/edit',
  method: 'post',
  data: payload,
  rawData: true,
});

// Board Governance Integrated
export const getBoardGovernanceIntegrated = (payload: {
  studio_ids: string[];
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/calendar-meeting/integrated',
  method: 'post',
  data: payload,
  rawData: true,
});

export interface BoardGovernanceStudioOption {
  id: string;
  name: string;
  cover: string;
}

/** Studios available for the standalone Board Governance filter. */
export const getBoardGovernanceStudios = (): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/list',
  method: 'post',
  rawData: true,
});

// Board Governance Share Meeting
export const shareBoardGovernanceMeeting = (payload: {
  meeting_id: string;
  share_types: string[]; // ["email", "wecom"]
  user_id: string[];
  studio_id: string;
  title: string;
  body?: string;
  page?: string;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/calendar-meeting/share',
  method: 'post',
  data: payload,
  rawData: true,
});

// Studios
interface StudioByIdPayload {
  studio_id: string;
  type?: 'revenue' | 'profit'; // Optional: Filter by revenue or profit
  datatime?: string; // Optional: Format "YYYY-MM" e.g., "2025-10"
}

export const getStudioOverviewById = (payload: StudioByIdPayload): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/overview',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getStudioTrendById = (payload: StudioByIdPayload): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/trend',
  method: 'post',
  data: {
    ...payload,
    type: payload?.type || 'revenue',
  },
  rawData: true,
});

export const getStudioFinancialsByYear = (payload: {
  studio_id: string;
  datatime?: string;
  start_date?: string;
  end_date?: string;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/yearly',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getStudioFinancialsByMonth = (payload: {
  studio_id: string;
  datatime?: string;
  start_date?: string;
  end_date?: string;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/monthly',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getStudioHeadcount = (payload: {
  studio_id: string;
  view_mode: 'by_month' | 'by_year';
  datatime?: string;
  start_date?: string;
  end_date?: string;
  include_breakdown?: boolean;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/headcount',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getStudioBurnRate = (payload: {
  studio_id: string;
  view_mode: 'by_month' | 'by_year';
  datatime?: string;
  start_date?: string;
  end_date?: string;
  include_breakdown?: boolean;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/burn-rate',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getStudioStaffRate = (payload: {
  studio_id: string;
  view_mode: 'by_month' | 'by_year';
  datatime?: string;
  start_date?: string;
  end_date?: string;
  include_breakdown?: boolean;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/staff-rate',
  method: 'post',
  data: payload,
  rawData: true,
});

/** Portfolio Overview burn rate — no studio_id; rows use studio_id/studio_name. */
export const getOverviewBurnRate = (payload: {
  view_mode: 'by_month' | 'by_year';
  datatime?: string;
  start_date?: string;
  end_date?: string;
  include_breakdown?: boolean;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/overview/burn-rate',
  method: 'post',
  data: payload,
  rawData: true,
});

/** Portfolio Overview headcount — no studio_id; rows use studio_id/studio_name. */
export const getOverviewHeadcount = (payload: {
  view_mode: 'by_month' | 'by_year';
  datatime?: string;
  start_date?: string;
  end_date?: string;
  include_breakdown?: boolean;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/overview/headcount',
  method: 'post',
  data: payload,
  rawData: true,
});

/** Portfolio Overview staff rate — no studio_id; rows use studio_id/studio_name. */
export const getOverviewStaffRate = (payload: {
  view_mode: 'by_month' | 'by_year';
  datatime?: string;
  start_date?: string;
  end_date?: string;
  include_breakdown?: boolean;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/overview/staff-rate',
  method: 'post',
  data: payload,
  rawData: true,
});

type TStudioEyesGetPayload = {
  op: 'get';
  key: string[];
};

type TStudioEyesSetPayload = {
  op: 'set';
  key: string;
  filter: string | number | Record<string, any>;
};

export const manageStudioEyesStatus = (
  payload: TStudioEyesGetPayload | TStudioEyesSetPayload,
): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/cond',
  method: 'post',
  data: payload,
  rawData: true,
});

// Studio In Dev Projects
export const getGateReviewManagementList = (payload: { project_id: string | number }): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/in_dev/gr_management/list',
  method: 'post',
  data: payload,
  rawData: true,
});

export const editGateReviewManagement = (payload: any): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/in_dev/gr_management/update',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getInDevProjectData = (payload: { project_id: string; datatime?: string }): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/in_dev/project',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getInDevFinancialsByPhase = (payload: { project_id: string; datatime?: string }): Promise<any> => https({
  url: '/api/v1/globalpedia_pc/mgmt/studio_management/in_dev_financials',
  method: 'get',
  params: payload,
  rawData: true,
});

export const getInDevFinancialsByMonth = (payload: {
  project_id: number,
  start_date: string,
  end_date: string,
  period: string,
  metrics: string[],
  logical_flag: string,
  datatime?: string
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/in_dev/metrics/list',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getInDevTitleSpendTrend = (payload: {
  project_id: string | number;
  view_mode: 'by_month' | 'by_phase';
  datatime?: string;
}): Promise<any> => https({
  // Dedicated endpoint for "In Dev Title Spend Trend ($)".
  // Backend path can be updated later without changing caller logic.
  url: '/api/v1/mgmt_pc/studio/in_dev/title_spend_trend',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getLifecycleFinancialForecast = (payload: { project_id: string; datatime?: string }): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/in_dev/life_cycle_forecast',
  method: 'post',
  data: payload,
  rawData: true,
});

export const editLifecycleFinancialForecast = (payload: any): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/in_dev/metrics/edit',
  method: 'post',
  data: payload,
  rawData: true,
});

export const editInDevProjectData = (payload: any): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/in_dev/metrics/edit',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getStudioNote = (payload: {
  studio_id: string;
  source: string;
  logical_flag?: string;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/note/get',
  method: 'post',
  data: payload,
  rawData: true,
});

export interface IStudiosNoteItem {
  studio_id: string;
  studio_name: string;
  cover: string;
  note: string;
  attach: string;
  operator: string;
  update_time: string;
}

export interface IStudiosNotesListPayload {
  date?: string;
}

export interface IStudiosNotesListResponse {
  date: string;
  studios: IStudiosNoteItem[];
}

export interface IStudiosNotesBatchUpdateItem {
  studio_id: string;
  note?: string;
  attach?: string;
}

export interface IStudiosNotesBatchUpdatePayload {
  date: string;
  notes: IStudiosNotesBatchUpdateItem[];
}

export interface IStudiosNotesBatchUpdateFailedItem {
  studio_id: string;
  studio_name: string;
  message: string;
}

export interface IStudiosNotesBatchUpdateResponse {
  success: boolean;
  failed: IStudiosNotesBatchUpdateFailedItem[];
}

export const getStudiosNotesList = (
  payload: IStudiosNotesListPayload = {},
): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/notes/list',
  method: 'post',
  data: payload,
  rawData: true,
});

export const batchUpdateStudiosNotes = (
  payload: IStudiosNotesBatchUpdatePayload,
): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/studios/notes/batch-update',
  method: 'post',
  data: payload,
  rawData: true,
});

// Studio Chart Events
export const getStudioChartEvents = (payload: {
  type: 'overview' | 'studios';
  studio_id?: string; // Required when type='studios'
  start_date: string; // Format: YYYY-MM
  end_date: string; // Format: YYYY-MM
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/event/get',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getLiveTitleEvents = (
  payload: { game_title: string },
): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/live-title/events',
  method: 'post',
  data: payload,
  rawData: true,
});

export const getGggFinanceOverview = (payload: {
  datatime: string;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/ggg-finance/overview',
  method: 'post',
  data: payload,
});

export const updateGggFinanceOverview = (payload: {
  orders?: Array<{ game_title: string; order: number }>;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/ggg-finance/overview/edit',
  method: 'post',
  data: payload,
  rawData: true,
});

export const downloadGggFinanceOverview = (payload: {
  start_date: string;
  end_date: string;
  by_platform: boolean;
}): Promise<any> => https({
  url: '/api/v1/mgmt_pc/studio/ggg-finance/overview/download',
  method: 'post',
  data: payload,
  rawData: true,
});
